import { produce } from "immer";
import { GlobalActions, resetAction } from "../../globals/store/actions";

import {
  getTiktokCreatorInfoFailureAction,
  getTiktokCreatorInfoInitAction,
  getTiktokCreatorInfoSuccessAction,
  getTiktokFailureAction,
  getTiktokInitAction,
  getTiktokLoginFailureAction,
  getTiktokLoginInitAction,
  getTiktokLoginSuccessAction,
  getTiktokProfilesFailureAction,
  getTiktokProfilesInitAction,
  getTiktokProfilesSuccessAction,
  getTiktokSuccessAction,
  hydrateTiktokDirectPostDraftAction,
  postBindTiktokFailureAction,
  postBindTiktokInitAction,
  postBindTiktokSuccessAction,
  postTiktokSocialPostFailureAction,
  postTiktokSocialPostInitAction,
  postTiktokSocialPostSuccessAction,
  postUnbindTiktokFailureAction,
  postUnbindTiktokInitAction,
  postUnbindTiktokSuccessAction,
  putTiktokSocialPostFailureAction,
  putTiktokSocialPostInitAction,
  putTiktokSocialPostSuccessAction,
  resetTiktokDirectPostUiAction,
  setTiktokDirectPostDraftAction,
  TiktokApiAction,
  TiktokDirectPostDraft,
} from "./api-actions";
import { TiktokSetting } from "../interface/api/post-tiktok-bind";
import { TiktokProfile } from "../interface/api/get-tiktok-profiles";
import { TiktokCreatorInfo } from "../interface/api/get-tiktok-creator-info";

/** Primera opción razonable si el usuario no eligió aún (evita dejar Publicar bloqueado). */
function pickDefaultPrivacyFromCreatorInfo(
  info: TiktokCreatorInfo | null | undefined
): string | null {
  const opts = info?.privacy_level_options;
  if (!Array.isArray(opts) || opts.length === 0) return null;
  if (opts.includes("PUBLIC_TO_EVERYONE")) return "PUBLIC_TO_EVERYONE";
  const nonSelf = opts.find((o) => o !== "SELF_ONLY");
  if (nonSelf) return nonSelf;
  return opts[0] ?? null;
}

export type { TiktokDirectPostDraft } from "./api-actions";

export const initialDirectPostDraft: TiktokDirectPostDraft = {
  privacyLevel: null,
  allowComment: false,
  allowDuet: false,
  allowStitch: false,
  commercialDisclosure: false,
  brandOrganic: false,
  brandContent: false,
  musicUsageConsent: false,
};

export interface TiktokState {
  settings: TiktokSetting | null;
  url: string;
  profiles: TiktokProfile[];
  error: string;
  creatorInfo: TiktokCreatorInfo | null;
  creatorInfoLoading: boolean;
  creatorInfoError: string;
  directPostDraft: TiktokDirectPostDraft;
}

const initialState: TiktokState = {
  settings: null,
  profiles: [],
  url: "",
  error: "",
  creatorInfo: null,
  creatorInfoLoading: false,
  creatorInfoError: "",
  directPostDraft: { ...initialDirectPostDraft },
};

export default produce(
  (
    draft: TiktokState = initialState,
    action: TiktokApiAction | GlobalActions
  ) => {
    switch (action.type) {
      case postBindTiktokSuccessAction.type:
      case getTiktokSuccessAction.type:
        draft.settings = action.settings;
        return draft;
      case postUnbindTiktokFailureAction.type:
      case postBindTiktokFailureAction.type:
      case getTiktokFailureAction.type:
      case getTiktokLoginFailureAction.type:
        draft.error = action.error;
        return draft;
      case getTiktokProfilesSuccessAction.type:
        draft.profiles = action.profiles;
        return draft;
      case postUnbindTiktokSuccessAction.type:
        draft.settings = null;
        draft.creatorInfo = null;
        draft.directPostDraft = { ...initialDirectPostDraft };
        return draft;
      case getTiktokLoginSuccessAction.type:
        draft.url = action.url;
        return draft;
      case getTiktokLoginInitAction.type:
        draft.url = "";
        return draft;
      case getTiktokCreatorInfoInitAction.type:
        draft.creatorInfoLoading = true;
        draft.creatorInfoError = "";
        return draft;
      case getTiktokCreatorInfoSuccessAction.type: {
        draft.creatorInfo = action.creatorInfo;
        draft.creatorInfoLoading = false;
        draft.creatorInfoError = "";
        const prev = draft.directPostDraft ?? { ...initialDirectPostDraft };
        const opts = action.creatorInfo?.privacy_level_options ?? [];
        let privacy = prev.privacyLevel;
        if (!privacy || (opts.length > 0 && !opts.includes(privacy))) {
          privacy = pickDefaultPrivacyFromCreatorInfo(action.creatorInfo);
        }
        draft.directPostDraft = {
          ...initialDirectPostDraft,
          ...prev,
          privacyLevel: privacy ?? prev.privacyLevel,
        };
        return draft;
      }
      case getTiktokCreatorInfoFailureAction.type:
        draft.creatorInfoLoading = false;
        draft.creatorInfoError = action.error;
        return draft;
      case setTiktokDirectPostDraftAction.type: {
        const current = draft.directPostDraft ?? { ...initialDirectPostDraft };
        draft.directPostDraft = {
          ...current,
          ...action.patch,
        };
        return draft;
      }
      case hydrateTiktokDirectPostDraftAction.type:
        draft.directPostDraft = { ...action.draft };
        return draft;
      case resetTiktokDirectPostUiAction.type:
        draft.creatorInfo = null;
        draft.creatorInfoLoading = false;
        draft.creatorInfoError = "";
        draft.directPostDraft = { ...initialDirectPostDraft };
        return draft;
      case postTiktokSocialPostSuccessAction.type:
      case putTiktokSocialPostSuccessAction.type:
        draft.directPostDraft = { ...initialDirectPostDraft };
        return draft;
      case resetAction.type:
        return initialState;
      case postBindTiktokInitAction.type:
      case postUnbindTiktokInitAction.type:
      case getTiktokInitAction.type:
      case putTiktokSocialPostInitAction.type:
      case putTiktokSocialPostFailureAction.type:
      case postTiktokSocialPostInitAction.type:
      case postTiktokSocialPostFailureAction.type:
      case getTiktokProfilesInitAction.type:
      case getTiktokProfilesFailureAction.type:
      default:
        return draft;
    }
  }
);
